// src/components/chat/ChatCreateGroupModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner, Alert, ListGroup } from 'react-bootstrap';
import { useChat } from '../../contexts/ChatContext';
import { usersAPI } from '../../services/api';

const ChatCreateGroupModal = ({ show, onHide, onGroupCreated }) => {
  const [groupName, setGroupName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const { createGroupRoom } = useChat();

  useEffect(() => {
    if (!show) {
      setGroupName('');
      setSearchTerm('');
      setSearchResults([]);
      setSelectedUsers([]);
      setError('');
    }
  }, [show]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      const data = await usersAPI.getUsers({ search: searchTerm.trim(), limit: 10 });
      const usersList = data.users || data.data || data;
      setSearchResults(Array.isArray(usersList) ? usersList : []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearching(false);
    }
  };

  const toggleUser = (user) => {
    setSelectedUsers(prev => {
      const exists = prev.some(u => u.id === user.id);
      if (exists) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleCreate = async () => {
    if (!groupName.trim()) {
      setError('Введите название группы');
      return;
    }
    if (selectedUsers.length === 0) {
      setError('Добавьте хотя бы одного участника');
      return;
    }
    setLoading(true);
    setError('');
    const room = await createGroupRoom(groupName, selectedUsers.map(u => u.id));
    if (room) {
      onGroupCreated(room);
    } else {
      setError('Не удалось создать группу');
    }
    setLoading(false);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-people me-2"></i>
          Создать группу
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Название группы</Form.Label>
          <Form.Control
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Введите название"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Добавить участников</Form.Label>
          <div className="d-flex gap-2 mb-2">
            <Form.Control
              type="text"
              placeholder="Поиск пользователей..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button variant="outline-primary" onClick={handleSearch} disabled={searching}>
              {searching ? <Spinner animation="border" size="sm" /> : 'Поиск'}
            </Button>
          </div>
          {searchResults.length > 0 && (
            <ListGroup className="mb-2">
              {searchResults.map(user => (
                <ListGroup.Item
                  key={user.id}
                  action
                  active={selectedUsers.some(u => u.id === user.id)}
                  onClick={() => toggleUser(user)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-person-circle"></i>
                    <div>
                      <div>{user.name}</div>
                      <small className="text-muted">@{user.telegram_username || 'нет username'}</small>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Form.Group>

        {selectedUsers.length > 0 && (
          <div className="mb-3">
            <Form.Label>Выбранные участники:</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {selectedUsers.map(user => (
                <Button
                  key={user.id}
                  variant="outline-primary"
                  size="sm"
                  onClick={() => toggleUser(user)}
                >
                  {user.name} <i className="bi bi-x ms-1"></i>
                </Button>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Отмена
        </Button>
        <Button variant="primary" onClick={handleCreate} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Создать группу'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChatCreateGroupModal;